import React, { useCallback, useEffect, useRef, useState } from 'react';
import { css, keyframes } from '@emotion/react';
import theme from '@/styles/theme';

// 페이드인 애니메이션
const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -40%); }
  to { opacity: 1; transform: translate(-50%, -50%); }
`;

// 페이드아웃 애니메이션
const fadeOut = keyframes`
  from { opacity: 1; transform: translate(-50%, -50%); }
  to { opacity: 0; transform: translate(-50%, -60%); }
`;

// 컨테이너 스타일
const containerStyle = (isVisible: boolean, isAnimating: boolean) => css`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  background: var(--whiteA90, rgba(255, 255, 255, 0.9));
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.1);
  animation: ${isAnimating ? (isVisible ? fadeIn : fadeOut) : 'none'} 0.3s
    ease-out;
  opacity: ${isVisible ? 1 : 0};
  white-space: normal; // 'nowrap'에서 'normal'로 변경
  width: 90%; // 최대 너비 설정
  height: auto; // 높이를 자동으로 설정
  min-height: 0; // 최소 높이 설정

  @media (max-width: ${theme.devices.md}px) {
    min-height: 0; // 최소 높이 설정
    height: auto; // 높이를 자동으로 설정
  }
`;

// 아이콘 컨테이너 스타일
const iconContainerStyle = css`
  width: 12px;
  height: 12px;
  position: relative;
  border-radius: 100px;
  flex-shrink: 0;
`;

// 아이콘 배경 스타일
const iconBackgroundStyle = css`
  width: 10.5px;
  height: 10.5px;
  position: absolute;
  left: 0.75px;
  top: 0.75px;
  background: #ff3a58;
  border-radius: 9999px;
`;

// 아이콘 느낌표 스타일
const iconExclamationStyle = css`
  width: 1.5px;
  height: 3.75px;
  position: absolute;
  left: 5.25px;
  top: 3px;
  background: white;
`;

// 아이콘 점 스타일
const iconDotStyle = css`
  width: 1.5px;
  height: 1.5px;
  position: absolute;
  left: 5.25px;
  top: 7.5px;
  background: white;
  border-radius: 9999px;
`;

// 텍스트 스타일
const textStyle = css`
  color: var(--blackA90, rgba(0, 0, 0, 0.9));
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  word-break: break-word;
  overflow-wrap: break-word;
`;

// 컴포넌트 props 인터페이스
interface ErrorToastProps {
  message: string;
  isShow: boolean;
  onHide: () => void;
}

const StoryGameUploadErrorToast: React.FC<ErrorToastProps> = ({
  message,
  isShow,
  onHide,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const showTimestampRef = useRef<number | null>(null);

  const hideToast = useCallback(() => {
    setIsAnimating(true);
    setIsVisible(false);
    setTimeout(() => {
      onHide();
      setIsAnimating(false);
    }, 300); // 애니메이션 종료 후 onHide 호출
  }, [onHide]);

  useEffect(() => {
    if (isShow && !isVisible) {
      // 토스트가 보이지 않다가 보이게 될 때
      setIsAnimating(true);
      setIsVisible(true);
      showTimestampRef.current = Date.now();

      // 애니메이션 종료 후 애니메이팅 상태 해제
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);

      // 3초 후에 hideToast 호출
      hideTimerRef.current = setTimeout(() => {
        hideToast();
      }, 3000);
    } else if (isShow && isVisible) {
      // 이미 보이고 있는 상태에서 또 show 되었을 때
      const elapsedTime = Date.now() - (showTimestampRef.current || 0);
      const remainingTime = Math.max(3000 - elapsedTime, 0);

      // 기존 타이머 취소 및 새 타이머 설정
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      hideTimerRef.current = setTimeout(() => {
        hideToast();
      }, remainingTime);
    } else if (!isShow && isVisible) {
      // hide 요청 시
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      hideToast();
    }

    return () => {
      // 클린업 함수
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [isShow, isVisible, hideToast]);

  // isVisible이 false이고 isShow도 false일 때만 null 반환
  if (!isVisible && !isShow) return null;

  return (
    <div css={containerStyle(isVisible, isAnimating)}>
      <div css={iconContainerStyle}>
        <div css={iconBackgroundStyle} />
        <div css={iconExclamationStyle} />
        <div css={iconDotStyle} />
      </div>
      <div css={textStyle}>{message}</div>
    </div>
  );
};

export default StoryGameUploadErrorToast;
